import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage } from 'react-intl';
import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Dot } from '@sorare/core/src/atoms/icons/Dot';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BrandM, BrandXL, LabelS } from '@sorare/core/src/atoms/typography';
import { S27Chip } from '@sorare/core/src/atoms/ui/Chip/S27Chip';
import { LeagueAvatar } from '@sorare/core/src/components/league/LeagueAvatar';
import { useIsTabletAndAbove } from '@sorare/core/src/hooks/device/useIsTabletAndAbove';
import { useIsVisibleInViewport } from '@sorare/core/src/hooks/ui/useIsVisibleInViewport';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';
import { ShopAllCards } from '@sorare/marketplace/src/components/new-season/ShopAllCards';
import { useMarketHomeFilters } from '@sorare/marketplace/src/hooks/useMarketHomeFilters';
import type { LeagueLaunch } from '@sorare/marketplace/src/lib/newSeason';
import {
  getLeagueAvatar,
  getLeagueBackgroundUrl,
  getLeagueLaunchSubtitle,
  getLeagueLaunchTitle,
  leagueLaunchAvatarInput,
} from '@sorare/marketplace/src/lib/newSeason';

import { LeagueRewards } from '#src/components/new-season/LeagueRewards/index.tsx';

import { TrendingCards } from './TrendingCards';
import type {
  ExpandedLeagueLaunch_competition,
  ExpandedLeagueLaunch_so5Competition,
} from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ gap: 3, center: true })`
  --bg-url: var(--c-nd-50);
  --min-width-left: 400px;
  position: relative;
  isolation: isolate;
  z-index: 0;
  padding: var(--double-unit);
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-150-opaque);
  background:
    linear-gradient(0deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%),
    linear-gradient(270deg, rgba(0, 0, 0, 0.7) 0%, rgba(0, 0, 0, 0) 100%),
    var(--bg-url) center / cover no-repeat;

  @container ${tabletAndAbove} {
    gap: calc(var(--unit) * 6);
    --container-padding: calc(var(--unit) * 6);
    padding: calc(var(--unit) * 6);
    flex-direction: row;
    justify-content: flex-start;
    align-items: stretch;
  }
`;

const LeagueAvatarWrapper = styled(Horizontal).attrs({ center: true })`
  width: calc(var(--unit) * 16);
  height: calc(var(--unit) * 16);
  > * {
    width: auto;
    height: 90px;
  }
`;
const Left = styled(Vertical).attrs({ center: true, gap: 2 })`
  flex-shrink: 1;
  @container ${tabletAndAbove} {
    min-width: var(--min-width-left);
    max-width: var(--min-width-left);
    justify-content: space-between;
  }
`;
const Right = styled(Vertical).attrs({ center: false, gap: 3 })`
  flex-grow: 1;
  max-width: 100%;
  @container ${tabletAndAbove} {
    max-width: calc(100% - var(--min-width-left) - calc(var(--unit) * 6));
  }
`;

const StyledBrandXL = styled(BrandXL)`
  max-width: 100%;
  word-break: break-word;
`;

type Props = {
  leagueLaunch: LeagueLaunch<
    ExpandedLeagueLaunch_competition | undefined,
    ExpandedLeagueLaunch_so5Competition | undefined
  >;
  isLive?: boolean;
};

export const ExpandedLeagueLaunch = ({ leagueLaunch, isLive }: Props) => {
  const isTabletAndAbove = useIsTabletAndAbove();
  const { handleRef, isVisible } = useIsVisibleInViewport<HTMLDivElement>();
  const {
    marketHomeFilters: { rarity },
  } = useMarketHomeFilters();
  const title = getLeagueLaunchTitle(leagueLaunch);
  const subtitle = getLeagueLaunchSubtitle(leagueLaunch, rarity);
  const leagueAvatar = getLeagueAvatar(leagueLaunchAvatarInput(leagueLaunch));
  const leagueNode =
    (!!leagueAvatar?.avatarUrl && (
      <LeagueAvatarWrapper>
        <LeagueAvatar league={leagueAvatar} />
      </LeagueAvatarWrapper>
    )) ||
    leagueLaunch?.iconNode;
  const backgroundUrl = getLeagueBackgroundUrl(leagueLaunch);

  return (
    <Root
      ref={handleRef}
      style={
        {
          '--bg-url': `url(${backgroundUrl})`,
        } as CSSProperties
      }
    >
      <Left>
        {!!isLive && (
          <S27Chip color="red">
            <Dot size={1} />
            <LabelS bold uppercase as="span">
              <FormattedMessage defaultMessage="Now live" />
            </LabelS>
          </S27Chip>
        )}
        {leagueNode}
        <Vertical gap={0.5} center>
          {!!subtitle && (
            <LabelS bold uppercase color="var(--c-nd-600)">
              {subtitle}
            </LabelS>
          )}
          <StyledBrandXL className="text-center">{title}</StyledBrandXL>
        </Vertical>
        <ShopAllCards
          league={leagueLaunch}
          color="secondary"
          size={isTabletAndAbove ? 'large' : 'small'}
        />
        {!!isTabletAndAbove && <LeagueRewards league={leagueLaunch} />}
      </Left>
      <Right>
        <BrandM>
          <FormattedMessage {...navLabels.trending} />
        </BrandM>
        <TrendingCards
          league={leagueLaunch}
          sport={Sport.FOOTBALL}
          skip={!isVisible}
        />
        {!isTabletAndAbove && leagueLaunch.sport === Sport.FOOTBALL && (
          <LeagueRewards league={leagueLaunch} />
        )}
      </Right>
    </Root>
  );
};

ExpandedLeagueLaunch.fragments = {
  competition: gql`
    fragment ExpandedLeagueLaunch_competition on Competition {
      slug
      displayName
      ...ShopAllCards_competition
      ...TrendingCards_competition
      ...getLeagueAvatar_competition
      ...getLeagueBackgroundUrl_competition
    }
    ${ShopAllCards.fragments.competition}
    ${TrendingCards.fragments.competition}
    ${getLeagueAvatar.fragments.competition}
    ${getLeagueBackgroundUrl.fragments.competition}
  ` as TypedDocumentNode<ExpandedLeagueLaunch_competition>,
  so5Competition: gql`
    fragment ExpandedLeagueLaunch_so5Competition on So5Competition {
      slug
      displayName
      ...getLeagueAvatar_so5Competition
      ...getLeagueBackgroundUrl_so5Competition
    }
    ${getLeagueAvatar.fragments.so5Competition}
    ${getLeagueBackgroundUrl.fragments.so5Competition}
  ` as TypedDocumentNode<ExpandedLeagueLaunch_so5Competition>,
};
